import React, { useState, useEffect } from "react";
import ShieldIcon from "./shield.png";
import ScaleIcon from "./scale.png";
import FolderIcon from "./folder.png";
import LockIcon from "./lock.png";
import Chat2Icon from "./chat2.png";
import GearIcon from "./gear.png";

const IndustrySolutionsSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const solutions = [
    {
      icon: <img src={ShieldIcon} alt="Healthcare" className="icon" />,
      heading: "Healthcare: PHIPA-Compliant Clinical AI",
      subheading: "Eliminate patient data exposure with on-premise AI, ensuring strict PHIPA adherence and enhancing clinical workflows.",
      points: [
        "**Clinical Documentation:** Accelerate documentation processes by 10x, freeing up valuable clinician time.",
        "**Discharge Summaries:** Generate accurate discharge summaries in seconds, improving patient throughput and reducing administrative burden.",
        "**Drug Interaction Checking:** Integrate built-in drug interaction checking to enhance patient safety and optimize treatment plans.",
        "**IPC Audit Readiness:** Maintain audit-ready status from day one with comprehensive logging and immutable records.",
      ],
    },
    {
      icon: <img src={ScaleIcon} alt="Financial Services" className="icon" />,
      heading: "Financial Services: Protected B Intelligence",
      subheading: "Leverage advanced AI for financial operations, aligned with OSFI and FINTRAC, without compromising data security or regulatory compliance.",
      points: [
        "**KYC/AML Processing:** Streamline Know Your Customer (KYC) and Anti-Money Laundering (AML) document processing with enhanced accuracy and speed.",
        "**Fraud Detection:** Implement robust fraud detection systems without exposing sensitive financial data to the cloud, maintaining complete privacy.",
        "**SOX Audit Trails:** Ensure complete Sarbanes-Oxley (SOX) audit trails with on-premise data control and verifiable record-keeping.",
        "**Canadian Jurisdiction:** Operate outside the jurisdiction of the U.S. CLOUD Act, securing your data under Canadian law.",
      ],
    },
    {
      icon: <img src={FolderIcon} alt="Legal Firms" className="icon" />,
      heading: "Legal Firms: Privilege-Preserving AI",
      subheading: "Empower your legal practice with AI that protects solicitor-client privilege, keeping sensitive case information strictly in-house.",
      points: [
        "**Contract Analysis:** Scale contract analysis capabilities, extracting key clauses and identifying risks with unprecedented efficiency.",
        "**Discovery Review:** Conduct comprehensive discovery reviews securely in-house, reducing reliance on external vendors and maintaining data control.",
        "**Matter-Based Access:** Implement granular, matter-based access control to ensure only authorized personnel access specific case data.",
        "**Complete Data Deletion:** Guarantee complete and verifiable deletion of client data on demand, upholding ethical obligations and privacy standards.",
      ],
    },
    {
      icon: <img src={LockIcon} alt="Government" className="icon" />,
      heading: "Government: Sovereign AI for Canada",
      subheading: "Deploy Protected B certified AI solutions that uphold Canadian sovereignty, ensuring data remains within national borders and under government control.",
      points: [
        "**ATIP Automation:** Automate Access to Information and Privacy (ATIP) requests, accelerating response times and reducing administrative overhead.",
        "**Bilingual Processing:** Support seamless bilingual processing (English/French) for all AI applications, serving Canada's diverse linguistic needs.",
        "**Air-Gapped Deployment:** Opt for air-gapped deployment to achieve ultimate security, isolating sensitive government data from all external networks.",
        "**Canadian-Eyes-Only:** Guarantee \"Canadian-eyes-only\" access, ensuring that all data and AI operations remain exclusively within Canadian jurisdiction.",
      ],
    },
    {
      icon: <img src={Chat2Icon} alt="Insurance" className="icon" />,
      heading: "Insurance: Underwriting Without Exposure",
      subheading: "Transform actuarial processes with private AI, safeguarding policyholder data and reducing regulatory risks associated with cloud exposure.",
      points: [
        "**Claims Processing:** Automate and accelerate claims processing, improving efficiency and reducing turnaround times.",
        "**Risk Assessment:** Enhance risk assessment capabilities with AI-driven insights, leading to more accurate underwriting decisions.",
        "**Policy Comparison:** Perform policy comparisons at scale, identifying optimal coverage and pricing strategies with speed and precision.",
        "**Provincial Privacy:** Ensure full compliance with all provincial privacy regulations, maintaining trust and avoiding penalties.",
      ],
    },
    {
      icon: <img src={GearIcon} alt="Manufacturing" className="icon" />,
      heading: "Manufacturing: IP-Protecting Operations AI",
      subheading: "Secure your intellectual property and optimize manufacturing operations with on-premise AI, preventing unauthorized access and maintaining competitive advantage.",
      points: [
        "**Quality Control:** Automate and enhance quality control processes, detecting defects and ensuring product consistency.",
        "**Supply Chain:** Optimize supply chain logistics, predicting disruptions and improving operational efficiency.",
        "**Predictive Maintenance:** Gain predictive maintenance insights to minimize downtime, extend equipment lifespan, and reduce operational costs.",
        "**Foreign Competitor Access:** Prevent foreign competitors from accessing your trade secrets and proprietary data, protecting your competitive edge.",
      ],
    },
  ];

  return (
    <div id="industry-solutions" className="section-container light-bg section-padding">
      <div className="text-center margin-bottom-50">
        <h2 className="section-title text-dark font-weight-700">
          Built for Canadian Compliance, Designed for Your Industry: Unlocking Private AI Advantage
        </h2>
        <p className="section-description text-medium-gray line-height-1-5">
          Libertus AI offers meticulously pre-configured, industry-specific solutions that deploy in days, not months. We understand the unique regulatory landscapes and operational demands of Canadian sectors, providing tailored AI platforms that ensure compliance, enhance efficiency, and safeguard your most sensitive data. Discover how our on-premise AI empowers your organization with intelligent capabilities, without compromising security or sovereignty.
        </p>
      </div>

      <div className="card-grid">
        {solutions.map((solution, index) => (
          <div key={index} className={`base-card hover-scale card-animation-on-load ${isVisible ? 'is-visible' : ''}`}>
            <div>
              <div className="card-icon" style={{ marginBottom: "20px" }}>{solution.icon}</div>
              <h3 className="card-title text-dark">
                {solution.heading}
              </h3>
              <p className="card-description text-medium-gray">
                {solution.subheading}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, marginTop: "20px" }}>
                {solution.points.map((point, pointIndex) => (
                  <li
                    key={pointIndex}
                    className="detail-item"
                  >
                    <span dangerouslySetInnerHTML={{ __html: point.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndustrySolutionsSection;
